import dayjs from "dayjs";
import {
  Bell,
  CalendarClock,
  Check,
  CheckCircle2,
  RotateCcw,
  Sparkles,
  XCircle
} from "lucide-react";
import type { FC, ReactNode } from "react";
import {
  useWatch,
  type SubmitHandler,
  type UseFormReturn
} from "react-hook-form";

import {
  CheckboxField,
  ControlField,
  DateField,
  FormRow,
  FormSheet,
  TextareaField,
  TextField
} from "@/components/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import {
  REMINDER_COLORS,
  type ReminderColor,
  type ReminderKind,
  type ReminderStatus
} from "@/api/types/foreignNationalReminder";
import { TeamMembersPicker } from "@/features/clientProfile/components/TeamMembersPicker";
import { EVENT_COLOR_CLASSES } from "../calendarModel";
import {
  DEFAULT_APPOINTMENT_MINUTES,
  KIND_LABEL,
  TIME_OPTIONS,
  addMinutesToTime,
  type CalendarReminder,
  type ReminderDraft
} from "../remindersModel";

const COLOR_LABEL: Record<ReminderColor, string> = {
  sky: "Sky",
  amber: "Amber",
  violet: "Violet",
  rose: "Rose",
  emerald: "Emerald",
  orange: "Orange"
};

/** Select of 15-minute steps bound to an `HH:mm` field. */
const TimeField: FC<{
  form: UseFormReturn<ReminderDraft>;
  name: "startTime" | "endTime";
  label: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
}> = ({ form, name, label, disabled, onChange }) => (
  <ControlField
    control={form.control}
    name={name}
    label={label}
    disabled={disabled}
    className="@md/form-row:max-w-36"
  >
    {(field, { id, invalid }) => {
      const value = String(field.value ?? "");
      const known = TIME_OPTIONS.some((o) => o.value === value);
      return (
        <Select
          value={value}
          onValueChange={(v) => {
            field.onChange(v);
            onChange?.(v);
          }}
          disabled={disabled}
        >
          <SelectTrigger
            id={id}
            ref={field.ref}
            className="w-full"
            aria-invalid={invalid || undefined}
            onBlur={field.onBlur}
          >
            <SelectValue placeholder="Time" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {!known && value ? (
              <SelectItem value={value}>
                {dayjs(`2000-01-01T${value}:00`).format("h:mm A")}
              </SelectItem>
            ) : null}
            {TIME_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }}
  </ControlField>
);

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<ReminderDraft>;
  onSubmit: SubmitHandler<ReminderDraft>;
  /** The record being edited, or null when creating. */
  record: CalendarReminder | null;
  isSaving: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onSetStatus: (status: ReminderStatus) => void;
  isStatusBusy: boolean;
  /**
   * Rendered above the title; the firm calendar uses it to pick which client
   * the entry belongs to. Inside a client's profile this is omitted.
   */
  clientField?: ReactNode;
  /** Extra line under the description (e.g. "Visible to the whole firm"). */
  subtitle?: ReactNode;
  /** False for a firm entry with no client: there is no portal to show it in. */
  canShareWithClient?: boolean;
};

/**
 * The create/edit side panel: kind toggle, title, start/end date + time,
 * all-day, location (appointments), color, assignees, notes. Existing entries
 * also get Complete / Re-open / Dismiss in the footer.
 */
export const EventSheet: FC<Props> = ({
  open,
  onOpenChange,
  form,
  onSubmit,
  record,
  isSaving,
  isDeleting,
  onDelete,
  onSetStatus,
  isStatusBusy,
  clientField,
  subtitle,
  canShareWithClient = true
}) => {
  const isEditing = record !== null;
  const systemManaged = Boolean(record?.system_managed);
  const isOpen = record?.status === "OPEN";
  const busy = isSaving || isDeleting || isStatusBusy;

  const [kind, allDay, startDate] = useWatch({
    control: form.control,
    name: ["kind", "allDay", "startDate"]
  });
  const isAppointment = kind === "APPOINTMENT";

  const setKind = (next: ReminderKind) => {
    const p = form.getValues();
    if (next === p.kind) return;
    form.setValue("kind", next, { shouldDirty: true });
    if (next === "APPOINTMENT" && (!p.endDate || !p.endTime)) {
      const end = addMinutesToTime(
        p.startDate,
        p.startTime,
        DEFAULT_APPOINTMENT_MINUTES
      );
      form.setValue("endDate", end.date, { shouldDirty: true });
      form.setValue("endTime", end.time, { shouldDirty: true });
    }
  };

  // Keep the end at least at the start when the start moves past it.
  const reconcileEnd = () => {
    const p = form.getValues();
    if (p.kind !== "APPOINTMENT") return;
    const startMs = Date.parse(`${p.startDate}T${p.startTime}:00`);
    const endMs = Date.parse(`${p.endDate}T${p.endTime}:00`);
    if (
      Number.isFinite(startMs) &&
      (!Number.isFinite(endMs) || endMs < startMs)
    ) {
      const end = addMinutesToTime(
        p.startDate,
        p.startTime,
        DEFAULT_APPOINTMENT_MINUTES
      );
      form.setValue("endDate", end.date, { shouldDirty: true });
      form.setValue("endTime", end.time, { shouldDirty: true });
    }
  };

  const noun = KIND_LABEL[kind ?? "REMINDER"].toLowerCase();

  return (
    <FormSheet
      form={form}
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? `Edit ${noun}` : `New ${noun}`}
      description={
        <>
          {isAppointment
            ? "A scheduled block of time, with a start and an end."
            : "Something to follow up on by a date or time."}
          {subtitle ? <span className="block">{subtitle}</span> : null}
        </>
      }
      onSubmit={onSubmit}
      isEditing={isEditing}
      isSubmitting={isSaving}
      isDeleting={isDeleting}
      onDelete={systemManaged ? undefined : onDelete}
      deleteTitle={`Delete this ${noun}?`}
      footerStart={
        isEditing ? (
          <div className="flex items-center gap-1">
            {isOpen ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => onSetStatus("COMPLETED")}
                >
                  {isStatusBusy ? (
                    <Spinner className="size-3.5" />
                  ) : (
                    <CheckCircle2 className="size-3.5" />
                  )}
                  Complete
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => onSetStatus("DISMISSED")}
                >
                  <XCircle className="size-3.5" />
                  Dismiss
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onSetStatus("OPEN")}
              >
                <RotateCcw className="size-3.5" />
                Re-open
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <ToggleGroup
        type="single"
        variant="outline"
        value={kind}
        onValueChange={(v) => {
          if (v) setKind(v as ReminderKind);
        }}
        className="w-full"
        aria-label="Type"
        disabled={systemManaged}
      >
        <ToggleGroupItem value="APPOINTMENT" className="flex-1 gap-1.5">
          <CalendarClock className="size-3.5" /> Appointment
        </ToggleGroupItem>
        <ToggleGroupItem value="REMINDER" className="flex-1 gap-1.5">
          <Bell className="size-3.5" /> Reminder
        </ToggleGroupItem>
      </ToggleGroup>

      {systemManaged ? (
        <Alert>
          <Sparkles className="size-4" />
          <AlertDescription>
            Generated from this client's dates. The title and date refresh
            automatically; you can still assign it, add notes, or close it.
          </AlertDescription>
        </Alert>
      ) : null}

      {clientField}

      <TextField<ReminderDraft>
        name="title"
        label="Title"
        required
        placeholder={
          isAppointment
            ? "e.g., Biometrics appointment"
            : "e.g., File H-1B extension"
        }
        readOnly={systemManaged}
        autoFocus={!isEditing}
      />

      <FormRow>
        <DateField<ReminderDraft>
          name="startDate"
          label={isAppointment ? "Start date" : "Date"}
          required
          disabled={systemManaged}
          onValueChange={reconcileEnd}
        />
        {!allDay ? (
          <TimeField
            form={form}
            name="startTime"
            label={isAppointment ? "Start time" : "Time"}
            disabled={systemManaged}
            onChange={reconcileEnd}
          />
        ) : null}
      </FormRow>

      {isAppointment ? (
        <FormRow>
          <DateField<ReminderDraft>
            name="endDate"
            label="End date"
            required
            min={startDate || undefined}
            disabled={systemManaged}
          />
          {!allDay ? (
            <TimeField
              form={form}
              name="endTime"
              label="End time"
              disabled={systemManaged}
            />
          ) : null}
        </FormRow>
      ) : null}

      <CheckboxField<ReminderDraft>
        name="allDay"
        label="All day"
        disabled={systemManaged}
      />

      {isAppointment ? (
        <TextField<ReminderDraft>
          name="location"
          label="Location"
          placeholder="e.g., USCIS Field Office, 2nd floor"
        />
      ) : null}
      {isAppointment && canShareWithClient ? (
        <CheckboxField<ReminderDraft>
          name="sharedWithClient"
          label="Show in the client's portal"
          description="They'll see the title, time and location under Coming up. Notes stay internal."
        />
      ) : null}

      <ControlField<ReminderDraft, "color"> name="color" label="Color">
        {(field) => (
          <div
            role="radiogroup"
            aria-label="Color"
            className="flex items-center gap-2"
          >
            {REMINDER_COLORS.map((c) => {
              const checked = field.value === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  aria-label={COLOR_LABEL[c]}
                  title={COLOR_LABEL[c]}
                  onClick={() => field.onChange(checked ? null : c)}
                  className={cn(
                    "flex size-7 items-center justify-center rounded-full transition-transform outline-none hover:scale-105 focus-visible:ring-2 focus-visible:ring-ring/50",
                    EVENT_COLOR_CLASSES[c].swatch,
                    checked &&
                      "ring-1 ring-fg-2 ring-offset-2 ring-offset-background"
                  )}
                >
                  {checked ? <Check className="size-3.5 text-white" /> : null}
                </button>
              );
            })}
          </div>
        )}
      </ControlField>

      <ControlField<ReminderDraft, "assignedToUserIds">
        name="assignedToUserIds"
        label="Assign to"
      >
        {(field) => (
          <TeamMembersPicker value={field.value} onChange={field.onChange} />
        )}
      </ControlField>

      <TextareaField<ReminderDraft>
        name="description"
        label="Notes"
        rows={4}
        placeholder="Add context or next steps..."
      />
    </FormSheet>
  );
};
